import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Bolt, Close, OpenInNew, Sync } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '@shared/services/api';
import type { Company } from '@/scenes/authentication/types/types.auth';
import type { FeatureMode } from '../featureOverrideSchema';
import { buildOverrides, modeForValue, FEATURE_GROUPS } from '../featureOverrideSchema';
import { FeatureOverridesForm, unknownOverrideKeys } from './FeatureOverridesForm';
import type { AdminPipelineSourceResult } from './AdminPlanPipelineRuns';
import { AdminPlanPipelineRuns, PipelineSourceChips } from './AdminPlanPipelineRuns';
import AdminEditCompany from './AdminEditCompany';
import { log } from '../../utils/log';

interface PlanFeatures {
  [key: string]: unknown;
}

/** Panel sections. Feature overrides is a long form, so it earns its own tab. */
type PanelTab = 'features' | 'integrations' | 'plans';

export interface AdminAccountRow {
  id: number;
  name: string;
  createdAt: string;
  ownerEmail: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscription: null | {
    id: number;
    planSlug: string;
    planName: string | null;
    status: string;
    term: string;
    startDate: string;
    endDate: string | null;
    featureOverrides: PlanFeatures | null;
    planFeatures: PlanFeatures;
    effectiveFeatures: PlanFeatures;
  };
}

interface AccountUserRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface AccountInviteRow {
  id: number;
  email: string;
  invitedAt: string;
  expiresAt: string;
}

interface IntegrationRow {
  provider: string;
  level: 'account' | 'user';
  connected: boolean | null;
  activeUserCount: number | null;
}

interface AccountPlanRow {
  id: number;
  publicId: string | null;
  name: string;
  status: string;
  companyId: number | null;
  companyName: string | null;
  createdAt: string;
  crmLinked: boolean;
}

interface AccountDetail {
  id: number;
  activeUsers: AccountUserRow[];
  invitedUsers: AccountInviteRow[];
  plans: AccountPlanRow[];
  activePlanCount: number;
  integrations: IntegrationRow[];
}

interface PlanScreen {
  planId: number;
  chatPromptsFromUser: number | null;
  competitorActivitySignals: number;
  contactsPinned: number;
  contactsSuggested: number;
  contactsWithSocialListening: number;
  crmLinked: boolean;
  offerings: number;
  opportunities: number;
  signalsPinned: number;
  signalsSuggested: number;
  stakeholderJobChangeSignals: number;
  todosPinned: number;
  todosSuggested: number;
}

const PROVIDER_LABELS: Record<string, string> = {
  google_calendar: 'Google Calendar',
  google_gmail: 'Gmail',
  microsoft_calendar: 'Microsoft Calendar',
  microsoft_mail: 'Microsoft Mail',
  salesforce: 'Salesforce',
};

/** A count with the underlying list behind a tooltip. */
function CountWithDetail({
  label,
  count,
  detail,
}: {
  label: string;
  count: number;
  detail: string[];
}): React.ReactElement {
  const body =
    detail.length === 0 ? (
      'None'
    ) : (
      <Box sx={{ py: 0.5 }}>
        {detail.map((line) => (
          <Typography key={line} variant="caption" display="block">
            {line}
          </Typography>
        ))}
      </Box>
    );

  return (
    <Tooltip title={body} arrow>
      <Paper variant="outlined" sx={{ px: 2, py: 1.25, cursor: detail.length ? 'help' : 'default', minWidth: 150 }}>
        <Typography variant="h5" fontWeight={600}>
          {count}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Paper>
    </Tooltip>
  );
}

function Metric({ label, value }: { label: string; value: number | string }): React.ReactElement {
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 128, flex: '1 1 128px' }}>
      <Typography variant="h6" fontWeight={600}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
        {label}
      </Typography>
    </Paper>
  );
}

export interface AdminAccountDetailPanelProps {
  account: AdminAccountRow | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

/**
 * The account panel behind a row click on the Accounts tab: who is in the
 * account, what is switched on, and a per-plan screen — plus the feature
 * overrides editor, which used to be a raw JSON textarea in its own drawer.
 */
export function AdminAccountDetailPanel({
  account,
  onClose,
  onSaved,
}: AdminAccountDetailPanelProps): React.ReactElement {
  const api = useApi();
  const [tab, setTab] = useState<PanelTab>('integrations');
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState<number | ''>('');
  const [screen, setScreen] = useState<PlanScreen | null>(null);
  const [screenLoading, setScreenLoading] = useState(false);
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [pipelineNote, setPipelineNote] = useState<null | string>(null);
  const [pipelineJob, setPipelineJob] = useState<null | { id: string; planId: number }>(null);
  const [pipelineSources, setPipelineSources] = useState<AdminPipelineSourceResult[] | null>(null);
  const [runsRefresh, setRunsRefresh] = useState(0);

  const [modes, setModes] = useState<Record<string, FeatureMode>>({});
  const [numbers, setNumbers] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // The plan's company, loaded on demand for the admin edit modal.
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [companyLoading, setCompanyLoading] = useState(false);

  const accountId = account?.id ?? null;
  const planFeatures = account?.subscription?.planFeatures ?? {};
  const storedOverrides = account?.subscription?.featureOverrides ?? null;

  // Seed the form from what is stored. Absent keys stay on "default" so saving
  // an untouched form is a no-op rather than a write of every key.
  useEffect(() => {
    const nextModes: Record<string, FeatureMode> = {};
    const nextNumbers: Record<string, string> = {};
    for (const group of FEATURE_GROUPS) {
      for (const feature of group.features) {
        const value = storedOverrides ? storedOverrides[feature.key] : undefined;
        nextModes[feature.key] = modeForValue(feature.kind, value);
        nextNumbers[feature.key] = typeof value === 'number' ? String(value) : '';
      }
    }
    setModes(nextModes);
    setNumbers(nextNumbers);
  }, [storedOverrides]);

  useEffect(() => {
    setTab('integrations');
    // A run being tracked belongs to the previous account — stop following it.
    setPipelineJob(null);
    setPipelineBusy(false);
    setPipelineSources(null);
    setPipelineNote(null);
    if (accountId == null) {
      setDetail(null);
      setScreen(null);
      setSelectedPlanId('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result: any = await api.get(`/admin/account/${accountId}/detail`);
      if (cancelled) return;
      if (result?.error) {
        setError(result.message ?? 'Failed to load account detail');
        setDetail(null);
      } else {
        setDetail(result?.data ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // `api` is a fresh object each render; depending on it would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  /** Load the plan's company and open the admin edit modal on it. */
  const handleEditCompany = useCallback(
    async (companyId: number) => {
      setCompanyLoading(true);
      try {
        const response: any = await api.get(`/admin/company/${companyId}`);
        const company = response?.data || response;
        if (company?.id) {
          setEditCompany(company);
        }
      } catch (err) {
        log.error('Error loading company for edit:', err);
      } finally {
        setCompanyLoading(false);
      }
    },
    // `api` is a fresh object each render — see the detail effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Persist company-detail edits from the modal — the modal's Save button
   * delegates persistence to its parent. The News and Coresignal tabs inside
   * it save themselves.
   */
  const handleSaveCompany = useCallback(
    async (updatedCompany: Partial<Company>) => {
      if (!updatedCompany.id) return;
      const result: any = await api.put(`/admin/company/${updatedCompany.id}`, updatedCompany);
      if (result?.error) {
        log.error('Error saving company:', result.message);
        return;
      }
      setEditCompany(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadScreen = useCallback(
    async (planId: number) => {
      if (accountId == null) return;
      setScreenLoading(true);
      setScreen(null);
      setPipelineNote(null);
      // Stop tracking any run started under the previously selected plan —
      // its completion note would otherwise land under this plan's heading.
      setPipelineJob(null);
      setPipelineBusy(false);
      setPipelineSources(null);
      const result: any = await api.get(`/admin/account/${accountId}/plan/${planId}/screen`);
      if (result?.error) {
        setError(result.message ?? 'Failed to screen plan');
      } else {
        setScreen(result?.data ?? null);
      }
      setScreenLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId],
  );

  const handleSave = async (): Promise<void> => {
    if (!account) return;
    setSaving(true);
    setError(null);
    const built = buildOverrides(modes, numbers);
    // Keys the schema does not describe are carried through untouched rather
    // than dropped — losing an override nobody modelled would be silent.
    for (const key of unknownOverrideKeys(storedOverrides)) {
      built[key] = (storedOverrides as PlanFeatures)[key];
    }
    const payload = Object.keys(built).length === 0 ? null : built;
    const result: any = await api.patch(`/admin/account/${account.id}/feature-overrides`, {
      featureOverrides: payload,
    });
    setSaving(false);
    if (result?.error) {
      setError(result.message ?? 'Failed to save overrides');
      return;
    }
    await onSaved();
  };

  const handleRunPipeline = async (): Promise<void> => {
    if (accountId == null || typeof selectedPlanId !== 'number') return;
    setPipelineBusy(true);
    setPipelineNote(null);
    setPipelineSources(null);
    const result: any = await api.post(`/admin/account/${accountId}/plan/${selectedPlanId}/signal-pipeline`, {});

    // A 409 means a run is already going, which is information rather than a
    // failure — say so plainly instead of surfacing it as an error.
    if (result?.error) {
      setPipelineBusy(false);
      setPipelineNote(result.message ?? 'Could not start the pipeline.');
      return;
    }

    const jobId: string | undefined = result?.data?.jobId;
    if (!jobId) {
      setPipelineBusy(false);
      setPipelineNote('Started. This runs in the background and takes a few minutes.');
      return;
    }

    setPipelineNote('Running. This takes a few minutes; per-source results appear here when it finishes.');
    setPipelineJob({ id: jobId, planId: selectedPlanId });
  };

  // Poll the run started above until it reaches a terminal state, then show
  // its per-source results and reload the pipeline-runs section below.
  useEffect(() => {
    if (!pipelineJob || accountId == null) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    const POLL_MS = 3000;
    const TIMEOUT_MS = 10 * 60 * 1000;

    const finish = (note: string, sources: AdminPipelineSourceResult[] | null): void => {
      setPipelineJob(null);
      setPipelineBusy(false);
      setPipelineNote(note);
      setPipelineSources(sources);
      setRunsRefresh((value) => value + 1);
    };

    const tick = async (): Promise<void> => {
      const result: any = await api.get(
        `/admin/account/${accountId}/plan/${pipelineJob.planId}/signal-pipeline/${pipelineJob.id}`,
      );
      if (cancelled) return;
      if (result?.error) {
        finish(result.message ?? 'Could not check on the run.', null);
        return;
      }
      const job = result?.data;
      const sources: AdminPipelineSourceResult[] | null = job?.output?.sources ?? null;
      if (job?.state === 'completed') {
        finish('Run complete.', sources);
        return;
      }
      if (job?.state === 'failed' || job?.state === 'cancelled') {
        finish(`Run ${job.state}.${job?.output?.message ? ` ${job.output.message}` : ''}`, sources);
        return;
      }
      if (Date.now() - startedAt > TIMEOUT_MS) {
        finish('Still running after 10 minutes — reload the pipeline runs below to check on it.', null);
        return;
      }
      timer = setTimeout(() => void tick(), POLL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // `api` keeps one identity for the panel's life; accountId can only change
    // by closing the panel, which also clears pipelineJob via loadScreen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineJob]);

  const handleSyncStripe = async (): Promise<void> => {
    if (!account) return;
    setSyncing(true);
    setError(null);
    const result: any = await api.get(`/admin/account/${account.id}/sync-stripe`);
    setSyncing(false);
    if (result?.data?.synced) await onSaved();
    else setError(result?.data?.reason ?? result?.message ?? 'Sync failed');
  };

  const unknownKeys = useMemo(() => unknownOverrideKeys(storedOverrides), [storedOverrides]);

  const selectedPlan = detail?.plans.find((plan) => plan.id === selectedPlanId) ?? null;

  return (
    <Drawer anchor="right" open={!!account} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 1000 } } }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6">{account?.name ?? 'Account'}</Typography>
            <Typography variant="body2" color="text.secondary">
              ID {account?.id} · created {account ? dayjs(account.createdAt).format('YYYY-MM-DD') : '-'} ·{' '}
              {account?.ownerEmail ?? 'no owner'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close account panel">
            <Close />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={account?.subscription?.planSlug ?? 'no subscription'}
            color={account?.subscription ? 'primary' : 'default'}
            variant="outlined"
          />
          <Chip
            size="small"
            label={account?.subscription?.status ?? 'none'}
            color={account?.subscription?.status === 'active' ? 'success' : 'default'}
            variant="outlined"
          />
          {account?.subscription?.term && <Chip size="small" label={account.subscription.term} variant="outlined" />}
          {account?.stripeCustomerId && (
            <Button
              size="small"
              endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
              href={`https://dashboard.stripe.com/customers/${account.stripeCustomerId}`}
              target="_blank"
              rel="noreferrer"
            >
              Stripe
            </Button>
          )}
          {account?.stripeCustomerId && (
            <Button
              size="small"
              startIcon={syncing ? <CircularProgress size={12} /> : <Sync sx={{ fontSize: 14 }} />}
              onClick={() => void handleSyncStripe()}
              disabled={syncing || saving}
            >
              {syncing ? 'Syncing…' : 'Sync with Stripe'}
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && <CircularProgress size={22} />}

        {detail && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
            <CountWithDetail
              label="Active users"
              count={detail.activeUsers.length}
              detail={detail.activeUsers.map((user) =>
                [user.firstName, user.lastName].filter(Boolean).length
                  ? `${[user.firstName, user.lastName].filter(Boolean).join(' ')} — ${user.email}`
                  : user.email,
              )}
            />
            <CountWithDetail
              label="Invited users"
              count={detail.invitedUsers.length}
              detail={detail.invitedUsers.map(
                (invite) => `${invite.email} — expires ${dayjs(invite.expiresAt).format('YYYY-MM-DD')}`,
              )}
            />
            <CountWithDetail
              label="Active account plans"
              count={detail.activePlanCount}
              detail={detail.plans.filter((plan) => plan.status === 'active').map((plan) => plan.name)}
            />
          </Stack>
        )}

        <Tabs
          value={tab}
          onChange={(_event, next) => setTab(next as PanelTab)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab value="integrations" label="Integrations" />
          <Tab value="plans" label="Account Plans" />
          <Tab value="features" label="Features" />
        </Tabs>

        {tab === 'integrations' &&
          (detail ? (
            <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
              {detail.integrations.map((integration) => {
                const label = PROVIDER_LABELS[integration.provider] ?? integration.provider;
                const active =
                  integration.level === 'account' ? !!integration.connected : (integration.activeUserCount ?? 0) > 0;
                const suffix =
                  integration.level === 'account'
                    ? integration.connected
                      ? 'connected'
                      : 'not connected'
                    : `${integration.activeUserCount} user${integration.activeUserCount === 1 ? '' : 's'}`;
                return (
                  <Chip
                    key={integration.provider}
                    size="small"
                    variant={active ? 'filled' : 'outlined'}
                    color={active ? 'success' : 'default'}
                    label={`${label} · ${suffix}`}
                  />
                );
              })}
            </Stack>
          ) : (
            !loading && (
              <Typography variant="body2" color="text.secondary">
                No integration data.
              </Typography>
            )
          ))}

        {tab === 'plans' &&
          (detail ? (
            <>
              <TextField
                select
                size="small"
                label="Account plan"
                value={selectedPlanId}
                onChange={(event) => {
                  const planId = Number(event.target.value);
                  setSelectedPlanId(planId);
                  void loadScreen(planId);
                }}
                sx={{ minWidth: 340, mb: 2 }}
              >
                {detail.plans.length === 0 && (
                  <MenuItem disabled value="">
                    No plans on this account
                  </MenuItem>
                )}
                {detail.plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                    {plan.companyName ? ` — ${plan.companyName}` : ''} ({plan.status})
                  </MenuItem>
                ))}
              </TextField>

              {screenLoading && <CircularProgress size={20} sx={{ display: 'block', mb: 2 }} />}

              {screen && (
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      color={screen.crmLinked ? 'success' : 'default'}
                      variant={screen.crmLinked ? 'filled' : 'outlined'}
                      label={screen.crmLinked ? 'Linked to CRM' : 'Not linked to CRM'}
                    />
                    {selectedPlan && <Chip size="small" variant="outlined" label={selectedPlan.status} />}
                    {selectedPlan?.publicId && (
                      <Chip size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} label={selectedPlan.publicId} />
                    )}
                    {selectedPlan && (
                      <Typography variant="caption" color="text.secondary">
                        Created on {dayjs(selectedPlan.createdAt).format('YYYY-MM-DD')}
                      </Typography>
                    )}
                    {selectedPlan?.companyId != null && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => void handleEditCompany(selectedPlan.companyId!)}
                        disabled={companyLoading}
                      >
                        {companyLoading ? <CircularProgress size={14} /> : 'Edit company'}
                      </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Re-runs news, SEC and reviews with caches and rate limits bypassed, and forces a re-sync of the plan owner's mail and calendar. Takes a few minutes.">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={pipelineBusy ? <CircularProgress size={12} /> : <Bolt sx={{ fontSize: 16 }} />}
                          onClick={() => void handleRunPipeline()}
                          disabled={pipelineBusy}
                        >
                          {pipelineBusy ? 'Running…' : 'Run signal pipeline'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  {pipelineNote && (
                    <Alert
                      severity={pipelineSources?.some((source) => source.status === 'failed') ? 'warning' : 'info'}
                      sx={{ mb: 1.5 }}
                      onClose={() => {
                        setPipelineNote(null);
                        setPipelineSources(null);
                      }}
                    >
                      {pipelineNote}
                      {pipelineSources && (
                        <Box sx={{ mt: 0.75 }}>
                          <PipelineSourceChips sources={pipelineSources} />
                        </Box>
                      )}
                    </Alert>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Metric label="Signals suggested" value={screen.signalsSuggested} />
                    <Metric label="Signals pinned" value={screen.signalsPinned} />
                    <Metric label="Contacts suggested" value={screen.contactsSuggested} />
                    <Metric label="Contacts pinned" value={screen.contactsPinned} />
                    <Metric label="Contacts w/ social listening" value={screen.contactsWithSocialListening} />
                    <Metric label="Action items suggested" value={screen.todosSuggested} />
                    <Metric label="Action items saved" value={screen.todosPinned} />
                    <Metric label="Opportunities" value={screen.opportunities} />
                    <Metric label="Offerings" value={screen.offerings} />
                    <Metric label="Competitor activity signals" value={screen.competitorActivitySignals} />
                    <Metric label="Stakeholder job-change signals" value={screen.stakeholderJobChangeSignals} />
                    <Metric
                      label="Chat prompts (user side)"
                      value={screen.chatPromptsFromUser === null ? '—' : screen.chatPromptsFromUser}
                    />
                  </Stack>

                  {accountId != null && (
                    <AdminPlanPipelineRuns accountId={accountId} planId={screen.planId} refreshKey={runsRefresh} />
                  )}
                </Box>
              )}
            </>
          ) : (
            !loading && (
              <Typography variant="body2" color="text.secondary">
                No plan data.
              </Typography>
            )
          ))}

        {tab === 'features' && (
          <>
            {!account?.subscription ? (
              <Alert severity="info">This account has no subscription, so there is nothing to override.</Alert>
            ) : (
              <>
                {unknownKeys.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Stored keys this form does not model: <code>{unknownKeys.join(', ')}</code>. They are preserved on
                    save but can only be changed in the database.
                  </Alert>
                )}
                <FeatureOverridesForm
                  planFeatures={planFeatures}
                  modes={modes}
                  numbers={numbers}
                  onModeChange={(key, mode) => setModes((prev) => ({ ...prev, [key]: mode }))}
                  onNumberChange={(key, raw) => setNumbers((prev) => ({ ...prev, [key]: raw }))}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button onClick={onClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
                    {saving ? 'Saving…' : 'Save overrides'}
                  </Button>
                </Stack>
              </>
            )}
          </>
        )}

        <AdminEditCompany
          isOpen={editCompany !== null}
          setIsOpen={() => setEditCompany(null)}
          company={editCompany}
          onSave={(updatedCompany) => void handleSaveCompany(updatedCompany)}
        />
      </Box>
    </Drawer>
  );
}

export default AdminAccountDetailPanel;
